import React, { useState, useEffect } from 'react';

/**
 * Package component showcasing the All-Inclusive Value Package
 * Carousel-style layout with auto-advancing slides and manual controls
 */
const Package = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(10);
  const [isPaused, setIsPaused] = useState(false);
  const [slideDirection, setSlideDirection] = useState('right');

  const packages = [
    {
      title: "One‑Time Development Fee",
      subtitle: "Fixed cost, no surprises, faster ROI",
      color: "#ff6b6b",
      details: [
        "Fixed, Upfront Cost: Get your complete software built, tested, and deployed with every feature and integration you need—no \"surprise\" line items as we go.",
        "No Recurring Charges: Forget monthly licenses or annual support renewals. Once it's live, it's 100% yours—use it however many seats you need, forever.",
        "Faster ROI: With no ongoing software bills, you recoup your investment in weeks through saved time and extra deals closed."
      ]
    },
    {
      title: "Seamless App Integrations",
      subtitle: "Connect everything, sync automatically",
      color: "#45b7d1",
      details: [
        "Instant Connections: Link your new software to WhatsApp, email platforms, marketing tools, and more—without costly middleware or duplicated work.",
        "Plug‑and‑Play Setup: We handle every API hookup and data sync so your sales, support, and marketing teams can start collaborating right away.",
        "Ongoing Flexibility: Need a new integration down the road? We've already built the foundation for a smooth, low‑cost add‑on."
      ]
    },
    {
      title: "On‑Premises Data Residency",
      subtitle: "Your data, your servers, your control",
      color: "#96ceb4",
      details: [
        "Your Servers, Your Rules: We install and configure the software directly on your infrastructure—keeping all customer data within your secure environment.",
        "Compliance‑Ready: Meet GDPR, HIPAA, SOC 2, or any internal policy with full transparency and audit logs.",
        "Peace of Mind: Maintain total ownership and control—no third‑party hosting, no shared clouds, no worries about data sovereignty."
      ]
    },
    {
      title: "Dedicated Support & Training",
      subtitle: "Personalized guidance every step of the way",
      color: "#ffeaa7",
      details: [
        "Personalized Onboarding: We guide your team through every feature with hands‑on workshops so everyone's confident from day one.",
        "Custom User Guides: Step‐by‐step materials tailored to your workflows ensure no one gets stuck.",
        "Priority Assistance: A direct support line with guaranteed response times keeps your operations smooth and interruption‑free."
      ]
    }
  ];

  // Auto-advance timer
  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          setSlideDirection('right');
          setActiveSlide((current) => (current + 1) % packages.length);
          return 10; // Reset to 10 seconds
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPaused, packages.length]);

  const handleSlideChange = (index, direction = 'right') => {
    setSlideDirection(direction);
    setActiveSlide(index);
    setTimeRemaining(10); // Reset timer
  };

  const handlePrevious = () => {
    const newIndex = activeSlide === 0 ? packages.length - 1 : activeSlide - 1;
    handleSlideChange(newIndex, 'left');
  };

  const handleNext = () => {
    const newIndex = (activeSlide + 1) % packages.length;
    handleSlideChange(newIndex, 'right');
  };

  return (
    <section id="package" className="section package reveal">
      <div className="section-header">
        <h2>All‑Inclusive Value Package</h2>
        <p>One simple investment gives you everything you need—no hidden fees, seamless connections, and total control over your data.</p>
      </div>

      <div className="package-carousel">
        {/* Navigation Arrows */}
        <button
          className="carousel-arrow carousel-arrow-left"
          onClick={handlePrevious}
          aria-label="Previous slide"
          title="Previous slide"
        >
          <i className="fas fa-chevron-left" aria-hidden="true"></i>
        </button>

        <button
          className="carousel-arrow carousel-arrow-right"
          onClick={handleNext}
          aria-label="Next slide"
          title="Next slide"
        >
          <i className="fas fa-chevron-right" aria-hidden="true"></i>
        </button>

        {/* Slide Container */}
        <div className="package-slide-container"
             onMouseEnter={() => setIsPaused(true)}
             onMouseLeave={() => setIsPaused(false)}>
          <div className={`package-slide slide-${slideDirection}`} key={`${activeSlide}-${slideDirection}`} style={{ '--accent-color': packages[activeSlide].color }}>

            <h3 className="slide-title">{packages[activeSlide].title}</h3>
            <p className="slide-subtitle">{packages[activeSlide].subtitle}</p>

            <ul className="slide-details">
              {packages[activeSlide].details.map((detail, index) => (
                <li key={index}>
                  <div className="detail-marker"></div>
                  <span dangerouslySetInnerHTML={{
                    __html: detail.replace(/^([^:]+:)/, '<strong>$1</strong>')
                  }}></span>
                </li>
              ))}
            </ul>

          </div>
        </div>

        {/* Slide Indicators with Timer */}
        <div className="carousel-indicators">
          {packages.map((_, index) => (
            <button
              key={index}
              className={`indicator ${activeSlide === index ? 'active' : ''}`}
              onClick={() => handleSlideChange(index)}
            >
              {activeSlide === index && (
                <svg
                  className="indicator-timer"
                  width="100%"
                  height="100%"
                  viewBox="0 0 50 50"
                >
                  <circle
                    cx="25"
                    cy="25"
                    r="23"
                    fill="none"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="2"
                  />
                  <circle
                    cx="25"
                    cy="25"
                    r="23"
                    fill="none"
                    stroke={`url(#indicatorGradient-${index})`}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="144"
                    strokeDashoffset={144 - (144 * (10 - timeRemaining)) / 10}
                    className="indicator-timer-progress"
                    transform="rotate(-90 25 25)"
                  />
                  <defs>
                    <linearGradient id={`indicatorGradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#ff6b6b" />
                      <stop offset="33%" stopColor="#e91e63" />
                      <stop offset="66%" stopColor="#9c27b0" />
                      <stop offset="100%" stopColor="#673ab7" />
                    </linearGradient>
                  </defs>
                </svg>
              )}
              <span className="indicator-label">{index + 1}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Package;